/**
 * Nutrition V2 — client-side data model.
 *
 * This intentionally diverges from the current server DTOs (`TemplateMenu` & friends):
 * the server is a later phase and will be built to match this shape. Known deltas to
 * carry over:
 *   - a substitution is a GROUP of foods here (`SubGroupV2`), not a single food
 *     (`TemplateFoodAlternative`)
 *   - a meal has a time RANGE (`timeFrom`/`timeTo`) — the server has no time at all
 *   - a meal can have whole alternative meals (`AltMealV2`) — no server equivalent
 *   - `fiber` is tracked everywhere — the server's `NutritionValues` has no fiber
 *   - company-level saved meals have no server entity yet
 *   - `isCustom` items (a free-calorie allowance) have no server equivalent: they need
 *     a food item with a NULL foodId carrying its own name + nutrition inline
 *   - company-scoped foods (`CompanyFoodV2`) have no server equivalent — `AdminFood`
 *     carries `certified` and `foodOrigin` but no company at all, so a food created by
 *     one company would be visible to every company. Until the server has a scope,
 *     `createCompanyFood` writes to the mock store.
 *
 * The calculator is a RECOMMENDATION, not a constraint — a menu is never validated
 * against its targets, and a trainer is free to save one that misses them entirely.
 *
 * NOT a delta: supplements. `AdminSupplement` is already company-scoped and
 * `TemplateSupplementItem` already carries dosage/timing/order, so `SupplementV2` maps
 * onto it one-to-one and the picker uses the REAL search endpoint.
 *
 * FOOD RESTRUCTURE (later phase) — a food should carry:
 *   name · image · macros (incl. fiber) · serving sizes · certified-by-us flag ·
 *   scope: system food vs company food · micronutrients (sugar, sodium, iron,
 *   vitamins, minerals…) per serving
 * `CatalogFood` today has name/brand/macros/servings/certified/foodOrigin but no
 * image, no fiber, no company scope and no micronutrients — micronutrients exist only
 * on the food-dictionary side (`ServingSizeInfo.micronutrients` / `ServingNutrientDto`,
 * see `foodDictionaryNutrientUtils`). `CompanyFoodV2` below is the shape that phase
 * should collapse into `CatalogFood`.
 */

import type { NutritionValues, ServingSizeInfo } from "../../../../api/generated/types.gen";
import type { CatalogFood, CatalogServingSize } from "../../../../services/catalogFood";

// ── types ───────────────────────────────────────────────────────────────────

export type MacroSet = {
  calories: number;
  protein: number;
  carbs: number;
  fats: number;
  fiber: number;
};

/** Totals also carry the protein that came from protein-dominant foods. */
export type MacroTotals = MacroSet & { qualityProtein: number };

export type MacroKey = "protein" | "carbs" | "fats";

/**
 * One vitamin / mineral / other micronutrient, mirroring the food dictionary's
 * `ServingNutrientDto`. The trainer-facing food API (`ServingSizeInfo`) does NOT carry
 * these yet — they live only behind `/system/food-dictionary` — so this reads empty
 * until the food restructure brings them over. See `readMicronutrients`.
 */
export type MicronutrientV2 = {
  code: string;
  nameHe?: string;
  nameEn?: string;
  unit?: string;
  category?: string;
  amount?: number;
};

export type ServingOption = {
  id?: number;
  servingName: string;
  gramsPerServing?: number;
  isDefault?: boolean;
  /** nutrition of ONE serving (this is how the server models it today) */
  nutrition: MacroSet;
  /** per ONE serving; empty until the food restructure exposes them */
  micronutrients?: MicronutrientV2[];
};

export type FoodItemV2 = {
  uid: string;
  foodId?: number;
  /** set instead of `foodId` when the food came from the company's own food list */
  /**
   * @deprecated Company foods now live in the same catalog as public ones and are referenced by
   * `foodId`. Kept only so a menu saved by an older tab still deserializes; nothing writes it.
   */
  companyFoodId?: string;
  name: string;
  brand?: string;
  /** empty until the food restructure adds images — see `readFoodImage` */
  imageUrl?: string;
  /**
   * The product's barcode, carried through from the food so the trainee's app can find this
   * exact food by scanning it. `AdminFood` already has the column; nothing used to read it.
   */
  barcode?: string;
  /**
   * Whether the figures were checked against a real label ("certified" on `AdminFood`).
   * Shown to the trainee as a provenance mark, so it has to survive into the menu — a food
   * that loses this on the way in is indistinguishable from one nobody verified.
   */
  verified?: boolean;
  /**
   * Not a food at all — a plain calorie allowance ("200 קל׳ לבחירתך"). It has no macro
   * breakdown and no serving sizes, so it exists only inside the menu that granted it.
   * A food the trainer types in is NOT this: it becomes a `CompanyFoodV2`.
   */
  isCustom?: boolean;
  servingSizeId?: number;
  servingName: string;
  quantity: number;
  /** nutrition of ONE serving of the currently selected serving size */
  nutrition: MacroSet;
  servings: ServingOption[];
  order: number;
  /**
   * The trainer typed this quantity, so nothing may recompute it.
   *
   * Only ever set on an item inside a substitution group: an alternative's quantity is normally
   * DERIVED from the food it replaces, and this records the one case where it is not. Changing that
   * alternative's serving size clears it — picking a different portion is itself a request to
   * recompute, and is the only way back to automatic.
   */
  quantityPinned?: boolean;
  /** each group is one "חלופה" and may hold several foods */
  substitutions: SubGroupV2[];
};

export type SubGroupV2 = {
  uid: string;
  name?: string;
  items: FoodItemV2[];
};

export type AltMealV2 = {
  uid: string;
  name: string;
  items: FoodItemV2[];
};

export type MealV2 = {
  uid: string;
  name: string;
  /** "HH:mm" */
  timeFrom: string;
  timeTo: string;
  notes?: string;
  /**
   * Whether the trainee sees this note. Absent means YES.
   *
   * The field states the EXCEPTION, not the rule: a note is written for the trainee unless
   * the trainer says otherwise, and every note written before this existed was. Storing the
   * rule instead would have made all of them private the day it shipped.
   */
  notesVisible?: boolean;
  order: number;
  items: FoodItemV2[];
  alternatives: AltMealV2[];
};

/**
 * A whole menu saved to the company library, used to start a new menu from.
 *
 * Carries its OWN targets and calculator, because that is how a template is authored: meals aimed
 * at a number. They describe the template, not a trainee — creating a menu from one copies the
 * meals alone, and the menu's calculator prefills from the trainee's real weight and program
 * instead, which no template can know.
 *
 * Targets are optional because the 599 templates migrated from V1 have none: V1 global templates
 * never held any, and zeroes there would read as a goal of nothing.
 */
export type MenuTemplateV2 = {
  id: string;
  name: string;
  targets?: MacroSet | null;
  qualityProteinPct?: number | null;
  /** kept so the calculator can be reopened and re-edited, as on a menu */
  calculator?: unknown | null;
  meals: MealV2[];
  createdDate: string;
};

/** A meal saved to the company library and reusable across menus. */
export type SavedMealV2 = {
  id: string;
  name: string;
  timeFrom?: string;
  timeTo?: string;
  items: FoodItemV2[];
  createdDate: string;
};

/**
 * A food the company created itself — same role as a food from the system database,
 * but owned by (and visible only to) the company that typed it in. Created from inside
 * a menu, then reusable in every menu through the normal search field.
 */
export type CompanyFoodV2 = {
  id: string;
  name: string;
  brand?: string;
  imageUrl?: string;
  /** so a food the trainer typed in can still be found by scanning it in the app */
  barcode?: string;
  /** at least one; the first is the default */
  servings: ServingOption[];
  createdDate: string;
};

/** What the create-food form collects, before the store assigns ids. */
export type CompanyFoodDraft = {
  name: string;
  brand?: string;
  barcode?: string;
  servings: Array<{ servingName: string; gramsPerServing?: number; nutrition: MacroSet }>;
};

/**
 * How the trainee receives the menu.
 *  - DETAILED_PLAN — meals built here, meal by meal
 *  - MACRO_GOALS   — daily targets only, the trainee eats what they like
 *  - PDF_PLAN      — a detailed menu the trainer already has as a file. The targets are
 *    still calculated (the app shows them as the daily summary), but the meals live in
 *    the PDF rather than in the builder.
 *
 * Fixed at creation: the type is chosen on the way out of the calculator and nothing edits it
 * afterwards. A menu of the wrong kind is created again as the right one.
 */
export type MenuTypeV2 = "DETAILED_PLAN" | "MACRO_GOALS" | "PDF_PLAN";

/**
 * Deliberately NOT "macros" for the PDF type: a trainer who has their own detailed menu
 * would never look under a macro heading for it.
 */
export const MENU_TYPE_LABELS: Record<MenuTypeV2, string> = {
  DETAILED_PLAN: "תפריט מפורט",
  MACRO_GOALS: "ערכים יומיים",
  PDF_PLAN: "תפריט PDF",
};

/** Types that show the daily-targets view instead of the meal builder. */
export const isMealBuilderType = (menuType: MenuTypeV2): boolean => menuType === "DETAILED_PLAN";

export type DayOfWeek =
  | "SUNDAY"
  | "MONDAY"
  | "TUESDAY"
  | "WEDNESDAY"
  | "THURSDAY"
  | "FRIDAY"
  | "SATURDAY";

/** Week order as it is displayed in Hebrew — Sunday first. */
export const ALL_DAYS: DayOfWeek[] = [
  "SUNDAY",
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
];

/**
 * Which weekday a date falls on, in the enum the menus use.
 *
 * Indexed off `Date.getDay()` rather than formatted from a locale: this app runs in Hebrew, and
 * `moment().format("dddd")` would answer "ראשון" — which matches no menu's `applicableDays`.
 */
export const dayOfWeek = (date: Date = new Date()): DayOfWeek => ALL_DAYS[date.getDay()];

/**
 * The menu the trainee is eating to on a given day.
 *
 * Several menus may be active at once, on purpose — the trainer offers options and the trainee
 * picks. But they are not interchangeable: each carries the days it applies to, so a Saturday menu
 * is not the answer on a Tuesday. Preference order is exactly that of the question being asked:
 * the menu the trainee SAYS they are on, then an active menu that covers the day, then any menu
 * that covers the day, then anything at all (a paused menu still tells a trainer what they have
 * written).
 *
 * The trainee's own answer comes first because it is the only one that is not a guess. Where two
 * menus are live for today, everything below this line is the server's sort order deciding for
 * them — which showed the trainer a plan their client had explicitly moved off. It still has to
 * cover the day: a standing choice does not make a Saturday menu the answer on a Tuesday, and the
 * server applies the same test before serving it.
 *
 * `applicableDays` missing or empty means the menu names no days. Those come last rather than
 * being read as "every day": a menu that says nothing about when it applies is not evidence that
 * it applies today, and one that does say so should win over it.
 */
export const menuForDay = (menus: MenuV2[], day: DayOfWeek = dayOfWeek()): MenuV2 | undefined => {
  const coversDay = (menu: MenuV2) => (menu.applicableDays || []).includes(day);
  return (
    menus.find((menu) => menu.chosenByTrainee && menu.active && coversDay(menu)) ||
    menus.find((menu) => menu.active && coversDay(menu)) ||
    menus.find(coversDay) ||
    menus.find((menu) => menu.active) ||
    menus[0]
  );
};

/**
 * A supplement prescribed alongside the menu. Supplements are the one part of V2 that
 * already exists server-side and is already company-scoped (`AdminSupplement.companyId`),
 * so they are picked from the company's real supplement list rather than typed in.
 * Shape mirrors `TemplateSupplementItem`; `notes` maps to its `description`.
 *
 * Deliberately NO order field: a supplement list is a set of instructions, not a
 * sequence like meals are, so `TemplateSupplementItem.supplementOrder` stays unused.
 */
export type SupplementV2 = {
  uid: string;
  supplementId?: number;
  name: string;
  imageUrl?: string;
  purchaseLink?: string;
  /** free text — "2 כפות", "5 גרם", "קפסולה אחת" */
  dosage?: string;
  /** free text — "בבוקר", "לפני אימון" */
  timing?: string;
  /** the trainer's note for THIS trainee, not the catalog description */
  notes?: string;
};

/** Common timings offered as one-click chips; the field itself stays free text. */
export const SUPPLEMENT_TIMINGS = [
  "בבוקר",
  "לפני אימון",
  "אחרי אימון",
  "עם הארוחה",
  "לפני השינה",
];

export const createSupplement = (input: {
  supplementId?: number;
  name: string;
  imageUrl?: string;
  purchaseLink?: string;
  dosage?: string;
  timing?: string;
}): SupplementV2 => ({
  uid: newId("supp"),
  supplementId: input.supplementId,
  name: input.name,
  imageUrl: input.imageUrl,
  purchaseLink: input.purchaseLink,
  dosage: input.dosage?.trim() || undefined,
  timing: input.timing?.trim() || undefined,
});

export type MenuV2 = {
  id: string;
  name: string;
  traineeId: string;
  applicableDays: DayOfWeek[];
  /**
   * Several menus may be active on the SAME day, on purpose: the trainer offers a few
   * options and the trainee picks whichever they want that day. Do not "fix" this into a
   * single-active-menu rule on the server.
   */
  active: boolean;
  /**
   * Whether this is the menu the TRAINEE says they are following — a different question from
   * `active`, which is the trainer's. Where several menus are live for the same day, this is the
   * one that is actually being eaten, and it is what the client card reports.
   *
   * Only the menus list answers it (the server's `listForTrainee`); it is absent everywhere a menu
   * is fetched on its own, so treat missing as "not stated" rather than as "no".
   */
  chosenByTrainee?: boolean;
  menuType: MenuTypeV2;
  targets: MacroSet;
  /**
   * How much of the protein target should come from protein-dominant foods, as a PERCENT
   * of `targets.protein`. Only meaningful for a menu with no meals — a detailed menu gets
   * its quality protein from the foods themselves. Optional: a menu may not set one.
   */
  qualityProteinPct?: number;
  /**
   * Daily hydration goal, in MILLILITRES.
   *
   * Not part of `targets`: water carries no energy and belongs to no macro budget, so
   * folding it in there would put a number in a set that is otherwise all kcal and grams.
   *
   * Optional, and the trainee's water card hides itself when it is absent — so a menu
   * written before this existed simply shows no water goal rather than showing zero.
   *
   * Matches `waterGoalMl` on the mobile client's `MenuV2` field for field, and the server's
   * column of the same name — it round-trips through `SaveMenuRequest` and comes back on
   * `MenuV2Dto`, which is what the trainee's water card reads its target from.
   */
  waterGoalMl?: number;
  /** free text shown alongside the whole menu — see `notesVisible` */
  notes?: string;
  /** whether the trainee sees the menu note. Absent means YES — see `MealV2.notesVisible`. */
  notesVisible?: boolean;
  /**
   * When the note was last written, ISO. The trainee's app prints it beside the note, so a
   * note with no date reads as timeless advice rather than as something the trainer said
   * this week. Stamped on save, only when the text actually changed.
   */
  notesDate?: string;
  /**
   * Who wrote this menu. Absent or 'COACH' for everything authored here.
   *
   * 'TRAINEE' is a menu the trainee wrote for themselves in the app — their own targets and
   * their own meals. The trainer must be able to SEE it (otherwise a trainee running their
   * own numbers is invisible) but never to edit it, so every write path here has to leave
   * it alone. See `isTraineeOwned`.
   */
  ownedBy?: "COACH" | "TRAINEE";
  /** PDF_PLAN only — the CDN url of the menu file the trainee opens in the app */
  pdfUrl?: string;
  pdfName?: string;
  /** kept so the calculator can be reopened and re-edited */
  calculator: unknown | null;
  meals: MealV2[];
  supplements: SupplementV2[];
  createdDate: string;
};

/**
 * Whether a note reaches the trainee. Absent means yes, so a note written before the
 * checkbox existed keeps behaving the way it always did.
 */
export const isNoteVisible = (owner: { notesVisible?: boolean }): boolean =>
  owner.notesVisible !== false;

/** A menu the trainee wrote for themselves: visible to the trainer, never editable by them. */
export const isTraineeOwned = (menu: Pick<MenuV2, "ownedBy">): boolean => menu.ownedBy === "TRAINEE";

/** A substitution within this many kcal of the original reads as a good swap. */
export const SUB_DELTA_TOLERANCE_KCAL = 30;

/**
 * How much quality protein a single meal is expected to carry.
 *
 * A rule of thumb, not a rule: roughly the per-meal dose that makes a day's protein useful
 * rather than merely present. It exists so a trainer can see, meal by meal, where a day that
 * hits its protein target got there from — a day can reach 180g of protein while three of its
 * five meals carry almost none, and the daily figure cannot show that.
 *
 * Deliberately NOT derived from `menu.targets.protein / meals.length`: meals are not equal
 * portions of a day, and dividing by the meal count would flag every snack.
 */
export const QUALITY_PROTEIN_PER_MEAL_G = 25;

// ── ids ─────────────────────────────────────────────────────────────────────

let idSeq = 0;

export const newId = (prefix = "id"): string =>
  `${prefix}_${(idSeq++).toString(36)}_${Math.floor(performance.now() * 1000).toString(36)}`;

// ── math ────────────────────────────────────────────────────────────────────

export const emptyMacros = (): MacroSet => ({
  calories: 0,
  protein: 0,
  carbs: 0,
  fats: 0,
  fiber: 0,
});

const round1 = (n: number): number => Math.round(n * 10) / 10;

/**
 * Where most of the food's ENERGY comes from — drives the quality-protein count.
 * Compared in CALORIES, not grams: 10g of fat (90 kcal) beats 20g of carbs (80 kcal).
 *
 * NOT the on-screen tag — see `dominantMacroByGrams`, which answers a different question
 * and is allowed to answer it differently.
 */
export const dominantMacro = (nutrition: Partial<MacroSet> | undefined): MacroKey => {
  const p = (nutrition?.protein || 0) * 4;
  const c = (nutrition?.carbs || 0) * 4;
  const f = (nutrition?.fats || 0) * 9;
  if (p <= 0) return c >= f ? "carbs" : "fats";
  if (p >= c && p >= f) return "protein";
  return c >= f ? "carbs" : "fats";
};

/**
 * The macro the food's TAG names — the one there is most of, in GRAMS.
 *
 * A tag is read against the grams printed beside it, and it loses an argument with them:
 * סינטה is 60g protein to 40g fat, and a tag reading שומן over those two numbers is
 * simply not believed. Energy is the honest answer to "where do the calories come from"
 * — fat carrying 9 kcal a gram means it wins that question at 40g against 60g of protein
 * (`dominantMacro`, above) — and the wrong answer to "what IS this food", which is what a
 * one-word label on a steak is taken to mean.
 *
 * Deliberately kept as a second function rather than a changed one: the quality-protein
 * count still wants the energy answer.
 *
 * Ties and empties resolve exactly as `dominantMacro` does — protein first, then carbs,
 * then fats, and a food with no macros at all reads 'carbs' — so the two rules only ever
 * differ where the unit itself makes them differ, never on an edge case.
 *
 * Mirrors progress-coach's `dominantMacroByGrams` (PR #193) name for name, so the two
 * codebases cannot drift on the rule a trainee and their trainer are both reading.
 */
export const dominantMacroByGrams = (nutrition: Partial<MacroSet> | undefined): MacroKey => {
  const p = nutrition?.protein || 0;
  const c = nutrition?.carbs || 0;
  const f = nutrition?.fats || 0;
  if (p <= 0) return c >= f ? "carbs" : "fats";
  if (p >= c && p >= f) return "protein";
  return c >= f ? "carbs" : "fats";
};

/** Nutrition of an item at its current quantity. */
export const itemNutrition = (item: FoodItemV2): MacroSet => {
  const q = item.quantity || 0;
  return {
    calories: Math.round((item.nutrition.calories || 0) * q),
    protein: round1((item.nutrition.protein || 0) * q),
    carbs: round1((item.nutrition.carbs || 0) * q),
    fats: round1((item.nutrition.fats || 0) * q),
    fiber: round1((item.nutrition.fiber || 0) * q),
  };
};

// ── calorie-matched alternatives ────────────────────────────────────────────
//
// An alternative exists to answer "what can I eat INSTEAD of this", and the answer is only useful
// if it is worth roughly the same. Making the trainer compute "how many eggs is 150g of chicken"
// by hand is how alternatives end up 200 kcal adrift without anyone noticing — the panel already
// prints that drift in red, which is an admission that it happens.
//
// So the quantity is derived: whenever an alternative is added, or its portion size changes, it is
// re-scaled to match the calories of the food it replaces. The trainer can always overrule it by
// typing a quantity, and that decision is remembered (`quantityPinned`) and never silently
// recomputed — including when the parent food changes.

/** Smallest quantity worth prescribing. Below this the row reads as a rounding artefact. */
const MIN_MATCHED_QUANTITY = 0.1;

/**
 * `alternative`, re-scaled so its calories match `target`'s.
 *
 * Returns it untouched when there is nothing to divide by: a food with no calories per serving (a
 * free vegetable, a zero-calorie drink) has no quantity that would make it equal 250 kcal, and
 * scaling towards infinity would be worse than leaving the trainer's number alone.
 */
export const matchedToCalories = (alternative: FoodItemV2, target: FoodItemV2): FoodItemV2 => {
  const perServing = alternative.nutrition?.calories || 0;
  const targetCalories = itemNutrition(target).calories;
  if (perServing <= 0 || targetCalories <= 0) return alternative;

  const quantity = Math.max(MIN_MATCHED_QUANTITY, round1(targetCalories / perServing));
  return quantity === alternative.quantity ? alternative : { ...alternative, quantity };
};

/**
 * Re-match every alternative of `item` that the trainer has not pinned.
 *
 * Called when the parent's own quantity or serving changes, because that changes the number the
 * alternatives are supposed to equal. A pinned alternative is left exactly as typed — the trainer
 * said "this much", and an edit elsewhere on the screen is not permission to overwrite it.
 */
/**
 * The name a substitution group carries while the trainer has not written one of their own: the
 * foods in it, joined. One food is "ביצה L"; stacking a second makes it "ביצה L + גבינה צהובה".
 */
export const autoGroupName = (items: FoodItemV2[]): string =>
  items
    .map((i) => i.name)
    .filter(Boolean)
    .join(" + ");

/**
 * Apply `items` to `group`, keeping the generated name in step with them — but ONLY while the name
 * is still the generated one. The moment the trainer types their own ("טבעוני", "ארוחת בוקר קלה"),
 * changing the foods must not overwrite it.
 */
export const withGroupItems = (group: SubGroupV2, items: FoodItemV2[]): SubGroupV2 => ({
  ...group,
  items,
  name:
    !group.name || group.name === autoGroupName(group.items) ? autoGroupName(items) : group.name,
});

/**
 * Swap an alternative with the food it stands in for: the alternative becomes the meal's food and
 * the food becomes the alternative. "הפוך לראשי".
 *
 * ── the uids stay with the POSITIONS, not with the foods ──
 * The new primary keeps the old primary's `uid` and the demoted food takes the promoted one's.
 * That is deliberate. A uid here names a SLOT — the row in the meal, the entry in the group — and
 * a trainee's day log points at those slots: `eaten` is keyed by the item uid and
 * `chosenSubstitutionByFood` maps an item uid to the group taken instead of it. Moving the uids
 * with the foods would leave a logged day pointing at an item that is now buried inside a group,
 * which resolves to nothing. Keeping them on the slots means a day log still refers to something
 * that exists; what it prescribes changes, which is what editing a menu always does, and days
 * already reported on froze their own copy of the plan and are untouched either way.
 *
 * ── both quantities are kept exactly as they are ──
 * An alternative's quantity is normally derived from the calories of the food it replaces, and the
 * promoted food's was. But the DEMOTED food's never was — it was the thing being matched TO, and
 * it holds the figure the trainer actually prescribed. Re-matching it on the way down would
 * silently rewrite "150 גרם" into whatever equals two eggs, so it comes to rest pinned: the
 * trainer's own number, which is exactly what `quantityPinned` is for. They can hand it back to
 * the matcher by changing its serving, the same as for any other alternative.
 *
 * Conversely the promoted food stops being pinned, because a primary is not matched to anything —
 * leaving the flag on would be a claim about a relationship that no longer exists.
 *
 * A group holding SEVERAL foods swaps in place: the demoted food takes the promoted one's seat, so
 * "chicken, or two eggs + cheese" promoted on the eggs becomes "eggs, or chicken + cheese". The
 * group keeps its size and its meaning, inverted — which is the only reading that does not quietly
 * discard one of the foods the trainer put there.
 *
 * Returns `item` untouched if the group or the food is not found, so a stale click is a no-op
 * rather than a corruption.
 */
export const promoteAlternative = (
  item: FoodItemV2,
  groupUid: string,
  subUid: string
): FoodItemV2 => {
  const groups = item.substitutions || [];
  const group = groups.find((g) => g.uid === groupUid);
  const promoted = (group?.items || []).find((s) => s.uid === subUid);
  if (!group || !promoted) return item;

  const demoted: FoodItemV2 = {
    ...item,
    uid: promoted.uid,
    order: promoted.order,
    // an alternative carries none of its own — they belong to whatever is primary
    substitutions: [],
    quantityPinned: true,
  };

  const { quantityPinned: _wasPinned, ...promotedRest } = promoted;

  return {
    ...promotedRest,
    uid: item.uid,
    order: item.order,
    substitutions: groups.map((g) =>
      g.uid === groupUid
        ? withGroupItems(
            g,
            (g.items || []).map((s) => (s.uid === subUid ? demoted : s))
          )
        : g
    ),
  };
};

export const withMatchedAlternatives = (item: FoodItemV2): FoodItemV2 => {
  const groups = item.substitutions || [];
  if (groups.length === 0) return item;
  return {
    ...item,
    substitutions: groups.map((group) => ({
      ...group,
      items: (group.items || []).map((alt) =>
        alt.quantityPinned ? alt : matchedToCalories(alt, item)
      ),
    })),
  };
};

export const listTotals = (items: FoodItemV2[] = []): MacroTotals =>
  items.reduce<MacroTotals>(
    (acc, item) => {
      const v = itemNutrition(item);
      acc.calories += v.calories;
      acc.protein = round1(acc.protein + v.protein);
      acc.carbs = round1(acc.carbs + v.carbs);
      acc.fats = round1(acc.fats + v.fats);
      acc.fiber = round1(acc.fiber + v.fiber);
      /*
       * GRAMS — the same rule the food's own tag uses, and a change from what stood here.
       *
       * This asked `dominantMacro`, which compares in CALORIES. Fat carries 9 kcal a gram against
       * protein's 4, so 250g of 9% cottage — 26g protein to 22.5g fat — answered "fats" and
       * contributed nothing, and so did a סינטה at 60g protein to 40g fat. The comment that stood
       * here called that "the intended outcome rather than an inconsistency"; it was an
       * inconsistency. A food has to be BOTH high in protein and lean before the energy rule lets
       * it count, which is a far narrower claim than "quality protein" makes, and it left the
       * builder printing "מתוכו איכותי" figures that ignored half the protein foods on the menu.
       *
       * `dominantMacroByGrams` already decides the tag, for the same reason (#193 on the app side:
       * a tag reading שומן over 60g of protein is not believed). That fix stopped at the label.
       * This is the counter catching up, so one food cannot be labelled חלבון and count as zero.
       *
       * A trainer's `qualityProteinPct` target is easier to meet than it was, because more foods
       * now qualify. Nothing stored changes — the server holds only the percentage and has never
       * decided which foods count. Mirrored in progress-coach so the trainee reads the same number.
       */
      if (dominantMacroByGrams(item.nutrition) === "protein") {
        acc.qualityProtein = round1(acc.qualityProtein + v.protein);
      }
      return acc;
    },
    { ...emptyMacros(), qualityProtein: 0 }
  );

export const mealTotals = (meal: MealV2): MacroTotals => listTotals(meal.items);

/**
 * Daily totals count the MAIN meals only — alternative meals and per-food
 * substitutions are choices, not additions.
 */
export const dayTotals = (menu: Pick<MenuV2, "meals">): MacroTotals =>
  (menu.meals || []).reduce<MacroTotals>(
    (acc, meal) => {
      const t = mealTotals(meal);
      acc.calories += t.calories;
      acc.protein = round1(acc.protein + t.protein);
      acc.carbs = round1(acc.carbs + t.carbs);
      acc.fats = round1(acc.fats + t.fats);
      acc.fiber = round1(acc.fiber + t.fiber);
      acc.qualityProtein = round1(acc.qualityProtein + t.qualityProtein);
      return acc;
    },
    { ...emptyMacros(), qualityProtein: 0 }
  );

/**
 * The menu's REAL numbers — what the trainee actually gets.
 *
 * The calculator only ever produced a RECOMMENDATION, so it is never the answer here:
 *  - a detailed menu is worth the sum of the meals in it
 *  - a macro-goals menu is worth the targets the trainer set
 *  - a PDF menu is worth the macros the trainer typed off the PDF
 */
/**
 * The menu's own contents AS a set of targets — what the trainer built, read as what they meant
 * to build.
 *
 * `dayTotals` answers "how much is in this menu"; this answers "what should the targets be if the
 * menu itself is the answer". Same numbers, minus `qualityProtein`, which is a property of the
 * FOODS (how much of the protein came from protein-dominant ones) and is meaningless as a goal.
 */
export const targetsFromMeals = (menu: Pick<MenuV2, "meals">): MacroSet => {
  const { calories, protein, carbs, fats, fiber } = dayTotals(menu);
  return { calories, protein, carbs, fats, fiber };
};

/** Two target sets that say the same thing. Used to leave state alone rather than re-set it. */
export const sameMacros = (a: MacroSet, b: MacroSet): boolean =>
  a.calories === b.calories &&
  a.protein === b.protein &&
  a.carbs === b.carbs &&
  a.fats === b.fats &&
  a.fiber === b.fiber;

export const menuMacros = (menu: Pick<MenuV2, "menuType" | "meals" | "targets">): MacroSet =>
  isMealBuilderType(menu.menuType) ? dayTotals(menu) : menu.targets;

export const subGroupTotals = (group: SubGroupV2): MacroTotals => listTotals(group.items);

/** How a whole substitution group compares to the food it replaces. */
export const subGroupDelta = (
  group: SubGroupV2,
  original: FoodItemV2
): { calories: number; protein: number; withinTolerance: boolean } => {
  const g = subGroupTotals(group);
  const o = itemNutrition(original);
  const calories = Math.round(g.calories - o.calories);
  return {
    calories,
    protein: round1(g.protein - o.protein),
    withinTolerance: Math.abs(calories) <= SUB_DELTA_TOLERANCE_KCAL,
  };
};

export const qualityProteinPercent = (totals: MacroTotals): number =>
  totals.protein > 0 ? Math.round((totals.qualityProtein / totals.protein) * 100) : 0;

// ── building items from search results ──────────────────────────────────────

/**
 * Fiber off a macro block, when the server sends it there.
 *
 * The V2 menu endpoints do (`MacroSetDto` carries it); the food-dictionary search does NOT —
 * `NutritionValues` has only the four macros. So this alone reports 0 for every food the builder
 * finds through search, which is why the catalogue's fiber never reached the screen. Pair it with
 * `fiberFromMicronutrients`, which reads the other half.
 */
export const readFiber = (values: NutritionValues | undefined): number =>
  Number((values as (NutritionValues & { fiber?: number }) | undefined)?.fiber) || 0;

/** The dictionary records fiber as a micronutrient under this code, in grams. */
const FIBER_CODE = "FIBER";

/**
 * Fiber as the food dictionary records it: a micronutrient, already scaled to THIS serving by the
 * server (a banana is 2.6 g per 100 g and 2.184 g for its 84 g unit, and both arrive that way).
 *
 * The catalogue has this for a minority of foods, but "a minority" is not "none" — a banana has it,
 * and the builder was printing "0 גר׳ סיבים" underneath it. A zero nobody measured reads exactly
 * like a measured zero, which is the whole reason to go and fetch the real number.
 */
export const fiberFromMicronutrients = (serving: ServingSizeInfo | undefined): number => {
  const found = readMicronutrients(serving).find((n) => n.code === FIBER_CODE);
  return Number(found?.amount) || 0;
};

/**
 * Same forward-compatible read as `readFiber`: `ServingSizeInfo` has no micronutrients
 * field today, so this returns [] — but it reads the field rather than hard-coding
 * empty, so the panel fills in the moment the server sends them.
 */
/**
 * Foods have no image anywhere in the API today — not on `CatalogFood`, not on a
 * serving. Read forward-compatibly (same trick as `readFiber`) so the thumbnails in the
 * search results and the food rows fill in on their own once the field exists.
 */
export const readFoodImage = (food: CatalogFood | undefined): string | undefined =>
  (food as (CatalogFood & { imageUrl?: string }) | undefined)?.imageUrl || undefined;

export const readMicronutrients = (
  serving: ServingSizeInfo | undefined
): MicronutrientV2[] => {
  const list = (serving as (ServingSizeInfo & { micronutrients?: MicronutrientV2[] }) | undefined)
    ?.micronutrients;
  return Array.isArray(list) ? list : [];
};

const servingNutrition = (serving: ServingSizeInfo | undefined): MacroSet => ({
  calories: serving?.nutritionValues?.calories || 0,
  protein: serving?.nutritionValues?.protein || 0,
  carbs: serving?.nutritionValues?.carbs || 0,
  fats: serving?.nutritionValues?.fats || 0,
  // the macro block first — a V2 menu serving carries fiber there — then the micronutrient the
  // food dictionary actually stores it in, which is the only source the search path has
  fiber: readFiber(serving?.nutritionValues) || fiberFromMicronutrients(serving),
});

export const toServingOptions = (food: CatalogFood): ServingOption[] => {
  const list = food.availableServingSizes || [];
  if (list.length > 0) {
    return list.map((s) => ({
      id: s.id,
      servingName: s.servingName || "מנה",
      gramsPerServing: s.gramsPerServing,
      isDefault: s.isDefault,
      nutrition: servingNutrition(s),
      micronutrients: readMicronutrients(s),
    }));
  }
  // fall back to the food's own default serving
  return [
    {
      id: food.servingSizeId,
      servingName: food.servingSizeName || "מנה",
      isDefault: true,
      nutrition: {
        calories: food.nutritionValues?.calories || 0,
        protein: food.nutritionValues?.protein || 0,
        carbs: food.nutritionValues?.carbs || 0,
        fats: food.nutritionValues?.fats || 0,
        fiber: readFiber(food.nutritionValues),
      },
    },
  ];
};

/** The single synthetic serving a custom item carries. */
export const CUSTOM_SERVING_NAME = "מנה";

/**
 * A food the trainer typed in by hand. Covers both "this food isn't in the database"
 * and the free-calorie case — "here are 200 kcal for this meal, spend them how you
 * like" — which is just a custom item with calories and no macros.
 */
export const createCustomItem = (input: {
  name: string;
  calories: number;
  protein?: number;
  carbs?: number;
  fats?: number;
  fiber?: number;
  quantity?: number;
  order?: number;
}): FoodItemV2 => {
  const nutrition: MacroSet = {
    calories: Number(input.calories) || 0,
    protein: Number(input.protein) || 0,
    carbs: Number(input.carbs) || 0,
    fats: Number(input.fats) || 0,
    fiber: Number(input.fiber) || 0,
  };
  return {
    uid: newId("custom"),
    name: input.name.trim() || "מזון חופשי",
    isCustom: true,
    servingSizeId: undefined,
    servingName: CUSTOM_SERVING_NAME,
    quantity: input.quantity ?? 1,
    nutrition,
    servings: [{ servingName: CUSTOM_SERVING_NAME, isDefault: true, nutrition }],
    order: input.order ?? 0,
    substitutions: [],
  };
};

export const pickServing = (
  servings: ServingOption[],
  servingSizeId?: number
): ServingOption | undefined =>
  servings.find((s) => s.id === servingSizeId) ||
  servings.find((s) => s.isDefault) ||
  servings[0];

export const foodToItem = (
  food: CatalogFood,
  opts: { servingSizeId?: number; quantity?: number; order?: number } = {}
): FoodItemV2 => {
  const servings = toServingOptions(food);
  const serving = pickServing(servings, opts.servingSizeId);
  return {
    uid: newId("food"),
    foodId: food.id,
    name: food.name || "",
    brand: food.brand,
    imageUrl: readFoodImage(food),
    barcode: food.barcode,
    verified: food.certified,
    servingSizeId: serving?.id,
    servingName: serving?.servingName || "מנה",
    quantity: opts.quantity ?? 1,
    nutrition: serving?.nutrition || emptyMacros(),
    servings,
    order: opts.order ?? 0,
    substitutions: [],
  };
};

/**
 * Same as `foodToItem`, for a food owned by the company rather than the system.
 *
 * It sets `foodId`, not a separate company id. A company food IS a catalog food — it lives in
 * `food_dictionary` with a `company_id` on it — so one reference reaches both kinds and the item
 * needs nothing to say which it was. That is also what lets the serving options be looked up the
 * same way for both.
 */
export const companyFoodToItem = (
  food: CompanyFoodV2,
  opts: { servingSizeId?: number; quantity?: number; order?: number } = {}
): FoodItemV2 => {
  const serving = pickServing(food.servings, opts.servingSizeId);
  return {
    uid: newId("food"),
    foodId: Number(food.id),
    name: food.name,
    brand: food.brand,
    imageUrl: food.imageUrl,
    barcode: food.barcode,
    // a company food is the company's own claim; only a system food is label-checked
    verified: false,
    servingSizeId: serving?.id,
    servingName: serving?.servingName || "מנה",
    quantity: opts.quantity ?? 1,
    nutrition: serving?.nutrition || emptyMacros(),
    servings: food.servings,
    order: opts.order ?? 0,
    substitutions: [],
  };
};

/** Re-point an item at a different serving size, keeping its quantity. */
export const withServing = (item: FoodItemV2, servingSizeId: number): FoodItemV2 => {
  const serving = pickServing(item.servings, servingSizeId);
  if (!serving) return item;
  return {
    ...item,
    servingSizeId: serving.id,
    servingName: serving.servingName,
    nutrition: serving.nutrition,
  };
};

// ── cloning (templates, duplication, copy-from-main) ────────────────────────

export const cloneItem = (item: FoodItemV2, order?: number): FoodItemV2 => ({
  ...item,
  uid: newId("food"),
  order: order ?? item.order,
  substitutions: (item.substitutions || []).map((g) => ({
    ...g,
    uid: newId("sub"),
    items: (g.items || []).map((i) => ({ ...i, uid: newId("food"), substitutions: [] })),
  })),
});

export const cloneItems = (items: FoodItemV2[] = []): FoodItemV2[] =>
  items.map((item, i) => cloneItem(item, i));

// ── empty factories ─────────────────────────────────────────────────────────

export const DEFAULT_MEAL_NAMES = [
  "ארוחת בוקר",
  "ביניים בוקר",
  "ארוחת צהריים",
  "ביניים אחה״צ",
  "ארוחת ערב",
  "ארוחת לילה",
];

export const DEFAULT_MEAL_TIMES: Array<[string, string]> = [
  ["07:00", "09:00"],
  ["10:00", "11:00"],
  ["12:30", "14:00"],
  ["16:00", "17:00"],
  ["19:00", "20:30"],
  ["21:30", "22:30"],
];

export const createMeal = (index: number, overrides: Partial<MealV2> = {}): MealV2 => {
  const [from, to] = DEFAULT_MEAL_TIMES[index] || ["", ""];
  return {
    uid: newId("meal"),
    name: DEFAULT_MEAL_NAMES[index] || `ארוחה ${index + 1}`,
    timeFrom: from,
    timeTo: to,
    order: index,
    items: [],
    alternatives: [],
    ...overrides,
  };
};

export const createMenu = (traineeId: string, overrides: Partial<MenuV2> = {}): MenuV2 => ({
  id: newId("menu"),
  name: "תפריט חדש",
  traineeId,
  // a new menu applies to the whole week — the trainer narrows it down, not the other
  // way around, so the common case (one menu, every day) needs no clicks
  applicableDays: [...ALL_DAYS],
  active: true,
  menuType: "DETAILED_PLAN",
  targets: emptyMacros(),
  calculator: null,
  // a menu starts with NO meals — the trainer adds them
  meals: [],
  supplements: [],
  createdDate: new Date().toISOString(),
  ...overrides,
});

export const MACRO_LABELS: Record<MacroKey, string> = {
  protein: "חלבון",
  carbs: "פחמימות",
  fats: "שומן",
};
